import { isBoolean, keys, min, omit, uniq } from 'lodash';

// Core
import {
  Activity,
  ActivityFormStateValues,
  BoardMocDetails,
  IBoardRemsDetails,
  IDatesAndLocation,
  IDictionary,
} from 'core/models';
import { IBCTBoard, IBCTCreditType } from 'layouts/pages/bct/types';

// Utils
import { transformCommercialSupportSources } from '../utils';

interface ITransformMocPayload {
  allowedMocKeys?: string[];
  amaCreditValue: number;
  boards: IDictionary<BoardMocDetails>;
  configs: IDictionary<IBCTBoard>;
}

interface ITransformActivityFormPayload {
  amaCreditId?: string;
  configDictionary?: IDictionary<IBCTBoard>;
  values: ActivityFormStateValues;
}

const filterCreditsForSubmit = (credits: IDictionary<boolean | number>): IDictionary<number> => {
  // If the checkbox was checked but no numbers were entered, the key's value will be 0
  // This prevents uncontrolled to controlled errors in console from Material UI
  // The isBoolean check filters the keys on credits used for the checkboxes true/false value
  const filteredCreditsList: string[] = keys(credits)?.filter(
    (key: string): boolean => !credits[key] || isBoolean(credits[key]),
  );
  return omit(credits, filteredCreditsList) as IDictionary<number>;
};

const transformMocBoards = ({
  allowedMocKeys,
  amaCreditValue,
  boards,
  configs,
}: ITransformMocPayload): IDictionary<BoardMocDetails> => {
  const updatedBoards: IDictionary<BoardMocDetails> = {};
  allowedMocKeys.forEach((boardKey: string) => {
    const board: BoardMocDetails = boards?.[boardKey];
    const config: IBCTBoard = configs?.[boardKey];
    const creditTypes: IBCTCreditType[] = config?.creditTypes;
    const isMatchAmaCreditValue: boolean = creditTypes?.some(({ mustMatchCmeAmount }) => mustMatchCmeAmount);
    const mocCreditValue: number = board?.mocPointsGiven || amaCreditValue;
    const updatedMocCreditValue: number = isMatchAmaCreditValue
      ? amaCreditValue
      : min([amaCreditValue, mocCreditValue]);
    updatedBoards[boardKey] = omit(
      {
        ...boards?.[boardKey],
        mocPointsGiven: updatedMocCreditValue,
      },
      'optionSelectionId',
    );
    // Get the default credit types into the boardMocDetails object for a given board
    const initialCreditTypes: string[] =
      creditTypes?.filter(({ default: isDefault }) => isDefault).map(({ id }) => id) || [];
    const currentCreditTypes: string[] = boards?.[boardKey]?.typesOfCreditIds || [];
    const updatedCreditTypes = uniq([...initialCreditTypes, ...currentCreditTypes]);
    if (updatedCreditTypes?.length) {
      updatedBoards[boardKey].typesOfCreditIds = updatedCreditTypes;
    }
  });
  return updatedBoards;
};

const transformRems = (
  rems: IDictionary<IBoardRemsDetails>,
  allowedRemsKeys: string[] = [],
): IDictionary<IBoardRemsDetails> => {
  const updatedRems: IDictionary<IBoardRemsDetails> = {};
  allowedRemsKeys.forEach((remsKey: string) => (updatedRems[remsKey] = { ...rems?.[remsKey] }));
  return updatedRems;
};

export const transformActivityFormValues = ({
  amaCreditId,
  configDictionary,
  values,
}: ITransformActivityFormPayload): Partial<Activity> => {
  const datesAndLocation: IDatesAndLocation = values.tempDatesAndLocation?.[0];
  const city = datesAndLocation?.city;
  const country = datesAndLocation?.country;
  const endDate = datesAndLocation?.endDate;
  const postalCode = datesAndLocation?.postalCode;
  const startDate = datesAndLocation?.startDate;
  const stateOrProvince = datesAndLocation?.stateOrProvince;
  const allowedMocKeys = values.boardMocDetailsSelection;
  const allowedRemsKeys = values.supportedRemsIds;
  const amaCreditValue: number = values.credits?.[amaCreditId] as number;
  const mocClaimDate = values?.mocCreditDeadline;
  const trueText = 'true';
  const isMoc = values.isMoc?.includes(trueText);
  const isRems = values.isRems?.includes(trueText);
  const isResettingMocData = !allowedMocKeys?.length && !isMoc;
  const transformedFormValues: Partial<Activity> = {
    ...values,
    boardMocDetails: isMoc
      ? transformMocBoards({
          allowedMocKeys,
          amaCreditValue,
          boards: values?.boardMocDetails,
          configs: configDictionary,
        })
      : {},
    boardRemsDetails: isRems ? transformRems(values.boardRemsDetails, allowedRemsKeys) : {},
    city,
    commercialSupportSources: transformCommercialSupportSources(
      values.commercialMonetarySupportSources,
      values.commercialInKindSupportSources,
    ),
    country,
    credits: filterCreditsForSubmit(values?.credits),
    endDate,
    hasCommercialSupport: values.hasCommercialSupport?.includes(trueText),
    includeInCmeFinder: values.includeInCmeFinder?.includes(trueText),
    isIpce: values.isIpce?.includes(trueText),
    isJointlyProvided: values.isJointlyProvided?.includes('join'),
    isMips: values.isMips?.includes(trueText),
    isMoc: values.isMoc?.includes(trueText),
    isPharmacyCertificateProgram: values.isPharmacyCertificateProgram?.includes(trueText),
    isRems: values.isRems?.includes(trueText),
    isRestrictedAudience: values.isRestrictedAudience === '' ? null : values.isRestrictedAudience?.includes('limited'),
    jointProviders: values?.jointProviders || [],
    markedForCommendationCriteria: values.markedForCommendationCriteria?.includes(trueText),
    mocCreditDeadline: !isResettingMocData ? mocClaimDate : null,
    mocProgramAttestation: !isResettingMocData
      ? values.mocProgramAttestation?.includes('mocProgramAttestation')
      : false,
    postalCode,
    startDate,
    stateOrProvince,
  };
  return transformedFormValues as Partial<Activity>;
};
